import {CloudAppOptionType} from 'interfaces'
import {AUTH_PROVIDER} from 'interfaces'

export const UUID_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

export const BOX_POSTFIX = '.box.com'

/** Inflight docs: Cloud Application Control (CAC) — GitHub-specific help link in UI. */
export const GITHUB_CAC_HELP_URL =
  'https://inflight.dope.security/dope.console/dope.swg-policy/cloud-application-control-cac/github'

/** RHP selection for the Global Settings view. */
export const CAC_RIGHT_PANEL_GLOBAL_SETTINGS = 'global-settings' as const

/** CAC app IDs that support the Global Settings right-panel view. */
export const CAC_GLOBAL_SETTINGS_APP_IDS = ['google', 'o365'] as const

export type CacGlobalSettingsAppId =
  (typeof CAC_GLOBAL_SETTINGS_APP_IDS)[number]

interface CacGlobalSettingsConfig {
  personalAccountsLabelKey: string
  aiLabelKey: string
  aiControl: 'gemini_restriction' | 'copilot_restriction'
  aiDataCy: string
}

/** Per-app copy and AI control shown in the Global Settings panel. */
export const CAC_GLOBAL_SETTINGS_CONFIG: Record<
  CacGlobalSettingsAppId,
  CacGlobalSettingsConfig
> = {
  google: {
    personalAccountsLabelKey: 'policies:CAC_SETTINGS_PERSONAL_GOOGLE_ACCOUNTS',
    aiLabelKey: 'policies:CAC_SETTINGS_GEMINI',
    aiControl: 'gemini_restriction',
    aiDataCy: 'cac-setting-gemini',
  },
  o365: {
    personalAccountsLabelKey:
      'policies:CAC_SETTINGS_PERSONAL_MICROSOFT_ACCOUNTS',
    aiLabelKey: 'policies:CAC_SETTINGS_COPILOT',
    aiControl: 'copilot_restriction',
    aiDataCy: 'cac-setting-copilot',
  },
}

/** RHP selection: the Global Settings sentinel, a configured value, or nothing. */
export type CacRightPanelSelection = string | null

export const cloudAppData: Record<string, CloudAppOptionType> = {
  o365: {
    displayName: 'Microsoft O365',
    prefix: '@',
    postfix: '',
    columnName: 'Email Domain or Tenant ID',
    entryInputKind: 'domain',
    descriptionTitle:
      'You can only log into O365 using these domains or tenant IDs.',
    enabledStateTitleForReadAccess:
      "You're viewing the allowed domains or tenant IDs for O365",
    notEnableStateTitleForReadAccess:
      'No domains or tenant IDs configured for O365',
    notEnableStateTitle:
      'If you enable Microsoft O365, you can only log into O365 using the listed email domains or tenant IDs.',
    subTitle: `*Configuring this control overrides any policy (content) settings.
    For example, if you've blocked the <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">Email</b> category, enabling this
    control will explicitly allow <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">O365</b> content.`,
    consumerAccountText:
      '*Personal accounts include @hotmail.com, @live.com, @msn.com, @passport.com, and @outlook.com',
  },
  [AUTH_PROVIDER.google]: {
    displayName: 'Google',
    prefix: '@',
    postfix: '',
    columnName: 'Email Domain',
    entryInputKind: 'domain',
    descriptionTitle: 'You can only log into Google using these domains.',
    enabledStateTitleForReadAccess:
      "You're viewing the allowed domains for Google",
    notEnableStateTitleForReadAccess: 'No domains configured for Google',
    notEnableStateTitle:
      'If you enable Google, you can only log into Google using these domains.',
    subTitle: ` *Configuring this control overrides any policy (content) settings.
    For example, if you've blocked the <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">Email</b> category, enabling this
    control will explicitly allow <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">Google</b> content.`,
    consumerAccountText:
      '*Personal accounts include @gmail.com and @googlemail.com',
  },
  box: {
    displayName: 'Box',
    prefix: '',
    postfix: BOX_POSTFIX,
    columnName: 'BOX SUBDOMAIN',
    entryInputKind: 'domain',
    notEnableStateTitle:
      'If you enable Box, you can only use the listed subdomains for Box.',
    notEnableStateTitleForReadAccess: 'No subdomains configured for Box',
    enabledStateTitleForReadAccess:
      "You're viewing the allowed subdomains for Box",
    descriptionTitle: 'You can only access Box using these domains.',
    subTitle: `*Configuring this control overrides any policy (content) settings.
    For example, if you've blocked the <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">File Storage</b> category, enabling this
    control will explicitly allow <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">Box</b> content.`,
  },
  salesforce: {
    displayName: 'Salesforce',
    prefix: '',
    postfix: '.my.salesforce.com',
    columnName: 'SALESFORCE SUBDOMAIN',
    entryInputKind: 'domain',
    notEnableStateTitle:
      'If you enable Salesforce, you can only access Salesforce using these subdomains.',
    notEnableStateTitleForReadAccess: 'No subdomain configured for Salesforce',
    enabledStateTitleForReadAccess:
      "You're viewing the allowed subdomains for Salesforce",
    descriptionTitle: 'You can only access Salesforce using these domains.',
    subTitle: `*Configuring this control overrides any policy (content) settings.
    For example, if you've blocked the <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">Business/Economy/IT</b> category, enabling this
    control will explicitly allow <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">Salesforce</b> content.`,
  },
  slack: {
    displayName: 'Slack',
    prefix: 'T0123',
    hidePrefix: true,
    postfix: '',
    columnName: 'ENTERPRISE/Workspace ID',
    entryInputKind: 'id',
    notEnableStateTitle:
      'If you enable this control for Slack, you can only log into these Slack workspaces.',
    notEnableStateTitleForReadAccess: 'No Slack workspaces configured',
    enabledStateTitleForReadAccess:
      "You're viewing the allowed Slack workspaces",
    descriptionTitle: 'You can only log into these Slack workspaces.',
    subTitle: `*Configuring this control overrides any policy (content) settings.
    For example, if you’ve blocked the <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">Chat/IM/SMS</b> category, enabling this
    control will explicitly allow <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">Slack</b> content.`,
  },
  github: {
    displayName: 'GitHub',
    prefix: '2386753',
    hidePrefix: true,
    postfix: '',
    columnName: 'Enterprise/Organization ID',
    entryInputKind: 'id',
    notEnableStateTitle:
      'If you enable this control for GitHub, users can only access the listed enterprises/organizations.',
    notEnableStateTitleForReadAccess:
      'No GitHub enterprises/organizations configured',
    enabledStateTitleForReadAccess:
      "You're viewing the allowed GitHub enterprises/organizations",
    descriptionTitle:
      'You can only access GitHub using these enterprises/organizations.',
    subTitle: `*Configuring this control overrides any policy (content) settings.
    For example, if you’ve blocked the <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">Developer Tools</b> category, enabling this
    control will explicitly allow <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">GitHub</b> content.`,
    consumerAccountText: '',
  },
  dropbox: {
    displayName: 'Dropbox',
    prefix: '2386753',
    hidePrefix: true,
    postfix: '',
    columnName: 'Dropbox ID',
    entryInputKind: 'id',
    notEnableStateTitle:
      'If you enable this control, you can only log into your specified Dropbox teams.',
    notEnableStateTitleForReadAccess: 'No Dropbox teams configured',
    enableStateTitleForReadAccess: "You're viewing the allowed Dropbox teams",
    descriptionTitle: 'You can only log into these Dropbox teams.',
    subTitle: `*Configuring this control overrides any policy (content) settings.
    For example, if you’ve blocked the <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">File Storage</b> category,
    enabling this control will explicitly allow <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">Dropbox</b> content.`,
  },
  webex: {
    displayName: 'Cisco WebEx',
    prefix: '@',
    postfix: '',
    columnName: 'Email Domain',
    entryInputKind: 'domain',
    notEnableStateTitle:
      'If you enable WebEx, you can only log into WebEx using these domains.',
    notEnableStateTitleForReadAccess: 'No domains configured for WebEx',
    enabledStateTitleForReadAccess:
      "You're viewing the allowed domains for WebEx",
    descriptionTitle: 'You can only log into WebEx using these domains.',
    subTitle: `*Configuring this control overrides any policy (content) settings.
    For example, if you’ve blocked the <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">Virtual Meetings</b> category,
    enabling this control will explicitly allow <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">WebEx</b> content.`,
  },
  chatgpt: {
    displayName: 'ChatGPT',
    prefix: '1234abcd-0000-1111-2222-abcdef123456', // Example UUID
    hidePrefix: true,
    postfix: '',
    columnName: 'ChatGPT Workspace ID',
    entryInputKind: 'id',
    notEnableStateTitle:
      'Enabling this control limits ChatGPT access to accounts with the specified workspace IDs.',
    notEnableStateTitleForReadAccess: 'No ChatGPT workspaces configured',
    enabledStateTitleForReadAccess: "You're viewing the allowed ChatGPT UUIDs",
    descriptionTitle:
      'You can only log into the corresponding ChatGPT accounts.',
    subTitle: `*This overrides other policy settings.
    For example, it allows <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">ChatGPT</b> even if the <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">AI/ML Applications</b> category is blocked.`,
    additionalInfo: 'This control is limited to ChatGPT Enterprise customers.',
  },
  claude: {
    displayName: 'Claude',
    prefix: '1234abcd-0000-1111-2222-abcdef123456', // Example UUID
    hidePrefix: true,
    postfix: '',
    columnName: 'Claude Organization ID',
    entryInputKind: 'id',
    notEnableStateTitle:
      'Enabling this control limits Claude access to accounts with the specified organization IDs.',
    notEnableStateTitleForReadAccess: 'No Claude organizations configured',
    enabledStateTitleForReadAccess: "You're viewing the allowed Claude UUIDs",
    descriptionTitle:
      'You can only log into the corresponding Claude accounts.',
    subTitle: `*This overrides other policy settings.
    For example, it allows <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">Claude</b> even if the <b class="text-white font-bold" style="color: white; font-family: ABC, sans-serif;">AI/ML Applications</b> category is blocked.`,
    additionalInfo: '',
  },
}

export const getCacEntryInputKind = (
  appId: string,
  value: string,
): CloudAppOptionType['entryInputKind'] => {
  if (appId === 'o365' && UUID_REGEX.test(value)) {
    return 'id'
  }

  return cloudAppData[appId]?.entryInputKind ?? 'domain'
}
